import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, isAxiosError } from "axios";
import queryClient from "@/queryClient";
import { clearCsrfToken, ensureCsrfToken, getCsrfToken } from "@/utils/csrf";

// Signs the app out once a refresh fails. The store registers it (store/store.ts): the store
// imports the services, so they never import the store.
let signOut: (() => void) | null = null;

export function setSignOutHandler(handler: () => void) {
  signOut = handler;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

const UNSAFE_METHODS = new Set(["post", "put", "patch", "delete"]);

/** Per-request timeout for multipart/file POSTs (browser → API → storage). Default `api` timeout stays 60s. */
export const UPLOAD_REQUEST_TIMEOUT_MS = 300_000;

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 60000,
  withCredentials: true,
});

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const method = (config.method || "get").toLowerCase();
    if (UNSAFE_METHODS.has(method)) {
      try {
        const token = getCsrfToken() || (await ensureCsrfToken());
        if (token && config.headers) {
          config.headers["X-CSRFToken"] = token;
        }
      } catch {
        // Let the request proceed; server will reject if CSRF is required.
      }
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    const url = originalRequest?.url || "";
    const method = (originalRequest?.method || "get").toLowerCase();
    const isAuthBootstrap =
      url.includes("/auth/login/") ||
      url.includes("/auth/refresh/") ||
      url.includes("/auth/csrf/") ||
      url.includes("/auth/logout/");
    const isSessionProbe = method === "get" && url.includes("/accounts/me/");
    if (isAuthBootstrap) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        await ensureCsrfToken();
        await axios.post(
          `${API_BASE_URL}/auth/refresh/`,
          {},
          {
            withCredentials: true,
            headers: {
              "X-CSRFToken": getCsrfToken() || "",
            },
          }
        );
        return api(originalRequest);
      } catch (refreshError) {
        clearCsrfToken();
        try {
          signOut?.();
        } catch {
          // ignore
        }
        queryClient.clear();
        // Session probe 401 means "not signed in", not "kick this URL to /login".
        if (!isSessionProbe && !window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

const GENERIC_WRAPPER_MESSAGES = new Set(["An error occurred", "Validation error"]);

const NON_FIELD_KEYS = new Set([
  "error",
  "code",
  "message",
  "details",
  "detail",
  "non_field_errors",
]);

function firstFieldErrorMessage(details: unknown): string | null {
  if (!details) return null;
  if (Array.isArray(details)) {
    const first = details.find((v) => typeof v === "string" && v);
    return typeof first === "string" ? first : null;
  }
  if (typeof details !== "object") return null;
  for (const value of Object.values(details as Record<string, unknown>)) {
    if (Array.isArray(value) && typeof value[0] === "string") return value[0];
    if (typeof value === "string" && value) return value;
  }
  return null;
}

function firstTopLevelFieldError(data: Record<string, unknown>): string | null {
  for (const [key, value] of Object.entries(data)) {
    if (NON_FIELD_KEYS.has(key)) continue;
    if (Array.isArray(value) && typeof value[0] === "string") return value[0];
    if (typeof value === "string" && value) return value;
  }
  return null;
}

const PASSWORD_DETAIL_KEYS = [
  "password",
  "new_password",
  "confirm_password",
  "current_password",
  "non_field_errors",
  "errors",
];

function collectStringMessages(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string" && !!item);
  }
  if (typeof value === "string" && value) return [value];
  return [];
}

/** Extract a human-readable message from our API error envelope (or raw DRF body). */
export function messageFromApiErrorBody(
  data: unknown,
  fallback = "An unexpected error occurred"
): string {
  if (!data || typeof data !== "object") return fallback;
  const body = data as Record<string, unknown>;

  if (typeof body.detail === "string" && body.detail) return body.detail;
  if (Array.isArray(body.detail) && typeof body.detail[0] === "string") return body.detail[0];

  const details =
    body.details && typeof body.details === "object" && !Array.isArray(body.details)
      ? (body.details as Record<string, unknown>)
      : body;

  const prioritized: string[] = [];
  for (const key of PASSWORD_DETAIL_KEYS) {
    prioritized.push(...collectStringMessages(details[key]));
  }
  if (prioritized.length) return prioritized.join(" ");

  // `details` itself, not the body it falls back to: a list (DRF's ValidationError("...")) is
  // read here, and the envelope's own `code` is never taken for a field message.
  const fromDetails = firstFieldErrorMessage(body.details);
  if (fromDetails) return fromDetails;

  if (
    typeof body.message === "string" &&
    body.message &&
    !GENERIC_WRAPPER_MESSAGES.has(body.message)
  ) {
    return body.message;
  }

  // Never treat the boolean `error: true` wrapper as a message.
  if (typeof body.error === "string" && body.error) return body.error;

  if (typeof body === "object" && !Array.isArray(body)) {
    const fromTopLevel = firstTopLevelFieldError(body);
    if (fromTopLevel) return fromTopLevel;
  }

  if (typeof body.message === "string" && body.message) return body.message;
  return fallback;
}

export const handleApiError = (error: AxiosError): string => {
  if (error.response?.data) {
    return messageFromApiErrorBody(
      error.response.data,
      error.message || "An unexpected error occurred"
    );
  }
  if (error.message) return error.message;
  return "An unexpected error occurred";
};

export const isNotFoundError = (error: unknown): boolean =>
  error instanceof AxiosError && error.response?.status === 404;

/** Readable message for any thrown value, preferring the server's own error text. */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) return handleApiError(error);
  if (error instanceof Error) return error.message;
  return "An unexpected error occurred";
};
/**
 * Why a file did not upload. With no answer (offline, cut off, timed out) axios says only "Network
 * Error", and a proxy's answer (empty, or an HTML or text page on a 502, 504 or edge 413) is no
 * reason either: all get the same plain line. Only the server's JSON body gives its reason.
 */
export function uploadErrorText(error: unknown, name: string): string {
  const plain = `Couldn't upload ${name}.`;
  const data: unknown = isAxiosError(error) ? error.response?.data : undefined;
  if (typeof data !== "object" || data === null) return plain;
  // A body with no reason of its own ({}, a list, the bare "An error occurred" wrapper) says
  // nothing more than the plain line.
  const reason = messageFromApiErrorBody(data, plain);
  return GENERIC_WRAPPER_MESSAGES.has(reason) ? plain : reason;
}

/** Map API `details` field errors to a flat field → message record. */
export function getApiFieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof AxiosError) || !error.response?.data) return {};
  const data = error.response.data as Record<string, unknown>;
  const details =
    data.details && typeof data.details === "object" && !Array.isArray(data.details)
      ? (data.details as Record<string, unknown>)
      : data;

  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(details)) {
    if (NON_FIELD_KEYS.has(key) && key !== "non_field_errors") continue;
    const messages = collectStringMessages(value);
    if (messages.length) result[key] = messages.join(" ");
  }
  return result;
}

/** Production password policy helper copy (min 12 chars + Django validators). */
export const PASSWORD_REQUIREMENTS_HINT =
  "Use at least 12 characters. Avoid common passwords, all-numeric passwords, and passwords too similar to your email.";
